"use client";

import {
  Children,
  cloneElement,
  forwardRef,
  type ButtonHTMLAttributes,
  type ReactElement,
} from "react";
import { cn } from "./cn";

const VARIANTS = {
  primary: "bg-primary text-primary-foreground hover:bg-primary-hover",
  secondary:
    "bg-surface-2 text-foreground border border-border hover:bg-muted",
  outline: "border border-border text-foreground hover:bg-muted",
  ghost: "text-foreground hover:bg-muted",
  destructive: "bg-destructive text-destructive-foreground hover:opacity-90",
} as const;

const SIZES = {
  sm: "h-8 px-3 text-xs gap-1.5",
  md: "h-9 px-4 text-sm gap-2",
  lg: "h-11 px-6 text-base gap-2",
} as const;

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Visual style. `primary` is the brand (indigo) action. */
  variant?: keyof typeof VARIANTS;
  /** Control height + padding. */
  size?: keyof typeof SIZES;
  /** Stretch to the width of the container. */
  fullWidth?: boolean;
  /**
   * Render the button's styling onto the single child element instead of a
   * `<button>` — for a `<Link>` that should look like a button. The child
   * keeps its own props; only the classes are merged (its `className` wins,
   * as it does for a plain Button).
   */
  asChild?: boolean;
}

/**
 * The standard action control. Token-based: theming and dark mode are
 * handled by the semantic colour tokens, never by `dark:` variants.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = "primary",
    size = "md",
    fullWidth,
    asChild,
    className,
    type = "button",
    children,
    ...props
  },
  ref,
) {
  const classes = cn(
    "inline-flex items-center justify-center rounded-control font-medium transition-colors select-none",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
    "disabled:pointer-events-none disabled:opacity-50",
    VARIANTS[variant],
    SIZES[size],
    fullWidth && "w-full",
    className,
  );

  if (asChild) {
    // `type` is deliberately not forwarded: it is a <button> attribute and
    // would land on an <a>. Neither is `ref` — reading it here is a render-phase
    // ref access (react-hooks/refs), and the child can take its own. The child's
    // own props win over ours, and its className goes last so cn() resolves
    // conflicts in the caller's favour.
    const child = Children.only(children) as ReactElement<{ className?: string }>;
    return cloneElement(child, {
      ...props,
      ...child.props,
      className: cn(classes, child.props.className),
    });
  }

  return (
    <button ref={ref} type={type} className={classes} {...props}>
      {children}
    </button>
  );
});
